import { useEffect, useMemo, useState, type ReactNode, type ComponentProps } from 'react'
import {
  Lock, Package, Download, RefreshCw, CheckCircle2, AlertTriangle, Plus, Trash2, Search,
  ChevronLeft, ChevronRight, HelpCircle, ExternalLink, Check, Boxes, Info, Eye, EyeOff,
  Copy, Sparkles, Rocket, BookOpen, Layers, SearchX, Upload,
  Megaphone, BarChart3, Target, ShoppingCart, ShieldCheck, Activity, type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import { useNavigate, useParams } from 'react-router-dom'
import { useOrganization } from '@/contexts/organization-context'
import {
  appsSdkService,
  type SdkFramework, type SdkComponent, type SdkBuild, type CredentialsStatus, type GenerateResult,
  type AppConfig, type VendorCredStatus, type SdkVersionInfo,
} from '@/services/appsSdk'

// Deep-link token in the URL: `<buildId>_<step>` (buildId is a hyphenated UUID, so the last `_`
// separates the step — no ambiguity). Lets a specific SDK + wizard tab open straight from a URL.
const buildTarget = (id: string, step: number) => `${id}_${step}`
const parseTarget = (t?: string): { id: string; step: number } | null => {
  if (!t) return null
  const i = t.lastIndexOf('_')
  if (i <= 0) return null
  const id = t.slice(0, i)
  const step = Number(t.slice(i + 1))
  if (!id || !Number.isInteger(step) || step < 1 || step > 4) return null
  return { id, step }
}

// "Base SDK v1.3.0 · published Jul 29, 2026" — the date is the registry publish timestamp.
const fmtSdkDate = (iso: string) => {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const CAPS: Record<string, { label: string; icon: LucideIcon; accent: string; dot: string }> = {
  ads: { label: 'Ads', icon: Megaphone, accent: 'text-fuchsia-600', dot: 'bg-fuchsia-500' },
  analytics: { label: 'Analytics', icon: BarChart3, accent: 'text-blue-600', dot: 'bg-blue-500' },
  attribution: { label: 'Attribution', icon: Target, accent: 'text-emerald-600', dot: 'bg-emerald-500' },
  iap: { label: 'In-app purchases', icon: ShoppingCart, accent: 'text-amber-600', dot: 'bg-amber-500' },
  privacy: { label: 'Privacy & consent', icon: ShieldCheck, accent: 'text-violet-600', dot: 'bg-violet-500' },
  observability: { label: 'Observability', icon: Activity, accent: 'text-rose-600', dot: 'bg-rose-500' },
}
const CAP_ORDER = ['ads', 'analytics', 'attribution', 'iap', 'privacy', 'observability']
const STEPS = [
  { n: 1, label: 'Basics', hint: 'Name & framework' },
  { n: 2, label: 'Vendors', hint: 'Pick capabilities' },
  { n: 3, label: 'Configure', hint: 'Accounts & apps' },
  { n: 4, label: 'Generate', hint: 'Review & build' },
]
const mb = (b: number | null | undefined) => `${((b || 0) / 1_000_000).toFixed(1)} MB`
const isMediator = (c: SdkComponent) => c.capability === 'ads' && !c.is_mediated

// Post-generate install guide — framework-specific (the bundle shape + install flow differ
// per framework). Add a case when a new FrameworkGenerator ships.
function installGuide(fwSlug: string): { pkg: string; steps: ReactNode[] } {
  if (fwSlug === 'unity') {
    return {
      pkg: 'com.glaium.sdk',
      steps: [
        <>Unzip, then copy <code>Packages/com.glaium.sdk/</code> into your Unity project's <code>Packages/</code> folder.</>,
        <>Copy <code>Assets/GlaiumIntegration/</code> into your project's <code>Assets/</code>.</>,
        <>Add <b>EDM4U</b> (External Dependency Manager for Unity), then run <i>Assets → External Dependency Manager → Android Resolver → Resolve</i> to pull the native SDKs in <code>Editor/GlaiumDependencies.xml</code>.</>,
        <>The SDK auto-initializes before the first scene from <code>Resources/glaium-sdk-config.json</code> — your keys are baked in.</>,
      ],
    }
  }
  // react_native (default)
  return {
    pkg: '@glaium/sdk-react-native',
    steps: [
      <>Unzip this bundle into your app (it includes <code>vendor/*.tgz</code> — the Glaium SDK — plus <code>src/config/</code>).</>,
      <>Merge this <code>package.json</code>'s deps into your app (the <code>@glaium/*</code> entries are <code>file:vendor/*.tgz</code>).</>,
      <>Run <code>npm install</code> — no registry or token needed.</>,
      <>Initialize once: <code>await GlaiumSDK.init(config)</code>.</>,
      <>Apply the native config from <code>native-manifest.json</code>, then build.</>,
    ],
  }
}

// Per-app SDK fields entered per app in the Builder (stored in apps[].vendors[slug]).
// Account-level keys (AppLovin SDK key, AppsFlyer dev key) live in Integrations (account
// picker). The Glaium gateway is auto-provisioned (not here). Keyed by integrator slug.
type PerAppField =
  | { kind: 'platform'; path: string; label: string; tip?: string; secret?: boolean; phIos?: string; phAndroid?: string }
  | { kind: 'single'; path: string; label: string; tip?: string; secret?: boolean; ph?: string }
  | { kind: 'firebase' }
  | { kind: 'adUnits' }
const PER_APP_FIELDS: Record<string, PerAppField[]> = {
  applovin_max: [{ kind: 'adUnits' }], // MAX ad units (banners by placement, etc.) — account SDK key is in Integrations
  // Google UMP (Privacy) = the opt-in GDPR consent request, run by MAX's consent flow. Google needs the app's
  // AdMob app ID natively even though no AdMob ads are served; the privacy policy URL is in App identity.
  ump: [
    { kind: 'platform', path: 'admobAppId', label: 'AdMob app ID (for GDPR consent)', tip: 'Google consent (UMP) runs through AppLovin MAX and needs your AdMob app ID — no AdMob ads or mediator. AdMob → Apps → App settings. Publish the GDPR message once in AdMob → Privacy & messaging, and turn on Google UMP in AppLovin → Privacy.', phIos: 'ca-app-pub-…~… (iOS)', phAndroid: 'ca-app-pub-…~… (Android)' },
  ],
  // AppsFlyer needs no per-app input: its iOS App ID = the App Store ID and its Android app id = the
  // package name, both already in App identity (store.ios / bundle.android). De-dup — no field here.
  facebook: [
    { kind: 'single', path: 'appId', label: 'Meta App ID', ph: 'e.g. 1353290903042016' },
    { kind: 'single', path: 'clientToken', label: 'Meta Client Token', secret: true },
  ],
  admob: [
    { kind: 'platform', path: 'appId', label: 'AdMob app ID', tip: 'AdMob → Apps → App settings. Also used by Google UMP consent when selected.', phIos: 'ca-app-pub-…~… (iOS)', phAndroid: 'ca-app-pub-…~… (Android)' },
    { kind: 'adUnits' },
  ],
  adapty: [
    { kind: 'single', path: 'appId', label: 'Adapty App ID', ph: 'Adapty app id' },
    { kind: 'single', path: 'publicKey', label: 'Adapty Public SDK Key', secret: true, ph: 'public_live_…' },
    { kind: 'single', path: 'notificationUrl', label: 'Adapty Apple notification URL', tip: 'Adapty → App Settings → iOS → App Store Server Notifications. Glaium\'s URL goes in App Store Connect for every app; we forward a verbatim copy here. Leave blank and Adapty receives nothing.', ph: 'https://api.adapty.io/api/v1/sdk/apple/webhook/…/' },
  ],
  firebase: [{ kind: 'firebase' }],
}
const AD_UNIT_TYPES = ['interstitial', 'interstitial_fallback', 'rewarded', 'banner', 'rewarded_interstitial', 'app_open']
const IAP_PRODUCT_TYPES = ['consumable', 'non_consumable', 'subscription']
// Entitlement presets — what a product unlocks. Most consumables (coins) grant nothing; a
// "Remove Ads" SKU grants ad-free; a premium unlock grants premium. Kept to the two the SDK
// acts on so customers don't have to invent flag strings.
const GRANT_OPTIONS: { key: string; label: string; desc: string }[] = [
  { key: 'adFree', label: 'Ad-free', desc: 'Removes ads (Remove Ads SKU)' },
  { key: 'premium', label: 'Premium', desc: 'Unlocks premium features' },
]
// Glaium's shared Play Developer API service account — customers invite THIS into their Play
// Console (no per-app key upload); the gateway validates Android purchases with its own key.
const GLAIUM_PLAY_VALIDATOR = 'google-play-iap-validator@glaium-mobile-apps.iam.gserviceaccount.com'
const perAppFieldsFor = (slug: string | null | undefined) => (slug && PER_APP_FIELDS[slug]) || []
const NO_AUTOFILL = { autoComplete: 'off', 'data-1p-ignore': true, 'data-lpignore': true } as const
const NO_PM = { 'data-1p-ignore': true, 'data-lpignore': true } as const // password-manager ignore (autoComplete set per field)

// Masked input with a reveal toggle (Stripe/Auth0/Supabase pattern).
function SecretField(props: ComponentProps<typeof Input>) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input {...props} type={show ? 'text' : 'password'} className={cn('pr-9', props.className)} />
      <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        aria-label={show ? 'Hide value' : 'Reveal value'}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

// ---- Framework logos (official marks) ----
const ReactNativeLogo = ({ className }: { className?: string }) => (
  <svg viewBox="-11.5 -10.23 23 20.46" className={className} fill="none" aria-hidden="true">
    <circle r="2" fill="currentColor" />
    <g stroke="currentColor" strokeWidth="1">
      <ellipse rx="11" ry="4.2" />
      <ellipse rx="11" ry="4.2" transform="rotate(60)" />
      <ellipse rx="11" ry="4.2" transform="rotate(120)" />
    </g>
  </svg>
)
const UnityLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="m12.9288 4.2939 3.7997 2.1929c.1366.077.1415.2905 0 .3675l-4.515 2.6076a.4192.4192 0 0 1-.4246 0L7.274 6.8543c-.139-.0745-.1415-.293 0-.3675l3.7972-2.193V0L1.3758 5.5977V16.793l3.7177-2.1456v-4.3858c-.0025-.1565.1813-.2682.318-.1838l4.5148 2.6076a.4252.4252 0 0 1 .2136.3676v5.2127c.0025.1565-.1813.2682-.3179.1838l-3.7996-2.1929-3.7178 2.1457L12 24l9.6954-5.5977-3.7178-2.1457-3.7996 2.1929c-.1341.082-.3229-.0248-.3179-.1838V13.053c0-.1565.087-.2956.2136-.3676l4.5149-2.6076c.134-.082.3228.0224.3179.1838v4.3858l3.7177 2.1456V5.5977L12.9288 0Z" />
  </svg>
)
function FrameworkLogo({ slug, className }: { slug: string; className?: string }) {
  if (slug === 'unity') return <UnityLogo className={className} />
  if (slug === 'react_native') return <ReactNativeLogo className={className} />
  return <Layers className={className} />
}
const frameworkSub = (slug: string) => (slug === 'unity' ? 'C# · UPM · EDM4U' : slug === 'react_native' ? 'JS · npm' : slug === 'flutter' ? 'Dart · pub' : '')

// ---- Vendor brand marks (deterministic monogram; scales as the catalog grows) ----
const BRAND_COLORS = [
  'bg-indigo-500', 'bg-rose-500', 'bg-emerald-600', 'bg-amber-500', 'bg-sky-500', 'bg-violet-500',
  'bg-fuchsia-600', 'bg-teal-600', 'bg-orange-500', 'bg-blue-600', 'bg-cyan-600', 'bg-pink-600',
]
function brandColor(slug: string) {
  let h = 0
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0
  return BRAND_COLORS[h % BRAND_COLORS.length]
}
function initials(name: string) {
  const w = (name || '').replace(/[^A-Za-z0-9 ]/g, ' ').trim().split(/\s+/).filter(Boolean)
  const s = ((w[0]?.[0] || '') + (w[1]?.[0] || '')).toUpperCase()
  return s || (name || '?').slice(0, 2).toUpperCase()
}
// Official vendor brand icons (simple-icons paths, viewBox 0 0 24 24), rendered white on
// the brand colour. Vendors simple-icons doesn't carry fall back to a monogram.
const VENDOR_ICONS: Record<string, { d: string; bg: string }> = {
  unity: { bg: '#000000', d: 'm12.9288 4.2939 3.7997 2.1929c.1366.077.1415.2905 0 .3675l-4.515 2.6076a.4192.4192 0 0 1-.4246 0L7.274 6.8543c-.139-.0745-.1415-.293 0-.3675l3.7972-2.193V0L1.3758 5.5977V16.793l3.7177-2.1456v-4.3858c-.0025-.1565.1813-.2682.318-.1838l4.5148 2.6076a.4252.4252 0 0 1 .2136.3676v5.2127c.0025.1565-.1813.2682-.3179.1838l-3.7996-2.1929-3.7178 2.1457L12 24l9.6954-5.5977-3.7178-2.1457-3.7996 2.1929c-.1341.082-.3229-.0248-.3179-.1838V13.053c0-.1565.087-.2956.2136-.3676l4.5149-2.6076c.134-.082.3228.0224.3179.1838v4.3858l3.7177 2.1456V5.5977L12.9288 0Z' },
  firebase: { bg: '#F57C00', d: 'M19.455 8.369c-.538-.748-1.778-2.285-3.681-4.569-.826-.991-1.535-1.832-1.884-2.245a146 146 0 0 0-.488-.576l-.207-.245-.113-.133-.022-.032-.01-.005L12.57 0l-.609.488c-1.555 1.246-2.828 2.851-3.681 4.64-.523 1.064-.864 2.105-1.043 3.176-.047.241-.088.489-.121.738-.209-.017-.421-.028-.632-.033-.018-.001-.035-.002-.059-.003a7.46 7.46 0 0 0-2.28.274l-.317.089-.163.286c-.765 1.342-1.198 2.869-1.252 4.416-.07 2.01.477 3.954 1.583 5.625 1.082 1.633 2.61 2.882 4.42 3.611l.236.095.071.025.003-.001a9.59 9.59 0 0 0 2.941.568q.171.006.342.006c1.273 0 2.513-.249 3.69-.742l.008.004.313-.145a9.63 9.63 0 0 0 3.927-3.335c1.01-1.49 1.577-3.234 1.641-5.042.075-2.161-.643-4.304-2.133-6.371m-7.083 6.695c.328 1.244.264 2.44-.191 3.558-1.135-1.12-1.967-2.352-2.475-3.665-.543-1.404-.87-2.74-.974-3.975.48.157.922.366 1.315.622 1.132.737 1.914 1.902 2.325 3.461zm.207 6.022c.482.368.99.712 1.513 1.028-.771.21-1.565.302-2.369.273a8 8 0 0 1-.373-.022c.458-.394.869-.823 1.228-1.279zm1.347-6.431c-.516-1.957-1.527-3.437-3.002-4.398-.647-.421-1.385-.741-2.194-.95.011-.134.026-.268.043-.4.014-.113.03-.216.046-.313.133-.689.332-1.37.589-2.025.099-.25.206-.499.321-.74l.004-.008c.177-.358.376-.719.61-1.105l.092-.152-.003-.001c.544-.851 1.197-1.627 1.942-2.311l.288.341c.672.796 1.304 1.548 1.878 2.237 1.291 1.549 2.966 3.583 3.612 4.48 1.277 1.771 1.893 3.579 1.83 5.375-.049 1.395-.461 2.755-1.195 3.933-.694 1.116-1.661 2.05-2.8 2.708-.636-.318-1.559-.839-2.539-1.599.79-1.575.952-3.28.479-5.072zm-2.575 5.397c-.725.939-1.587 1.55-2.09 1.856-.081-.029-.163-.06-.243-.093l-.065-.026c-1.49-.616-2.747-1.656-3.635-3.01-.907-1.384-1.356-2.993-1.298-4.653.041-1.19.338-2.327.882-3.379.316-.07.638-.114.96-.131l.084-.002c.162-.003.324-.003.478 0 .227.011.454.035.677.07.073 1.513.445 3.145 1.105 4.852.637 1.644 1.694 3.162 3.144 4.515z' },
  admob: { bg: '#4285F4', d: 'M11.46.033h-.052A11.993 11.993 0 0 0 0 11.922v.052c0 7.475 6.563 11.928 11.447 11.928h.17a3.086 3.086 0 0 0 3.125-3.047c0-1.693-1.433-2.917-3.152-2.917h-.039a6.016 6.016 0 0 1-5.508-6.368v-.052a6.016 6.016 0 0 1 5.573-5.509c1.719 0 3.125-1.237 3.125-2.917A3.086 3.086 0 0 0 11.604.02h-.143zm2.031.026a3.516 3.516 0 0 1 1.746 3.021 3.386 3.386 0 0 1-1.928 3.047c2.865.6 4.532 3.126 4.688 5.378v7.684a3.49 3.49 0 0 1 6.003.026v-7.736A12.046 12.046 0 0 0 13.491.045zm7.475 17.932a2.995 2.995 0 1 0 .04 0z' },
  meta: { bg: '#0866FF', d: 'M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z' },
  tiktok: { bg: '#000000', d: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
  apple: { bg: '#000000', d: 'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' },
  google: { bg: '#4285F4', d: 'M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z' },
}
// integrator slug -> brand-icon key (several vendors share a parent brand)
const VENDOR_ICON_KEY: Record<string, string> = {
  unity: 'unity', admob: 'admob', firebase: 'firebase', crashlytics: 'firebase',
  facebook: 'meta', tiktok_acquisition: 'tiktok',
  apple_search_ads: 'apple', att: 'apple', ump: 'google',
}
function BrandMark({ name, slug, className }: { name: string; slug: string; className?: string }) {
  const icon = VENDOR_ICONS[VENDOR_ICON_KEY[slug] || '']
  if (icon) {
    return (
      <div className={cn('flex items-center justify-center rounded-md shrink-0 p-1', className)} style={{ backgroundColor: icon.bg }}>
        <svg viewBox="0 0 24 24" fill="#fff" className="h-full w-full" aria-hidden="true"><path d={icon.d} /></svg>
      </div>
    )
  }
  return (
    <div className={cn('flex items-center justify-center rounded-md text-white font-semibold leading-none shrink-0', brandColor(slug), className)}>
      {initials(name)}
    </div>
  )
}

export function AppsSdkPage() {
  const { hasAppsSdkAccess } = useRoleAccess()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id
  const orgSlug = currentOrganization?.slug
  const canAccess = hasAppsSdkAccess() // Manager/Admin/Owner — matches the backend's require_admin (widened role set)
  const navigate = useNavigate()
  const { target } = useParams() // `<buildId>_<step>` deep-link token, or undefined for the list

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Post-regenerate banner in the list — mirrors the wizard's step-4 outcome (success + the same
  // "…not connected — won't function until connected" credential warning).
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; text: string } | null>(null)
  const [frameworks, setFrameworks] = useState<SdkFramework[]>([])
  const [components, setComponents] = useState<SdkComponent[]>([])
  const [componentsFor, setComponentsFor] = useState<string | null>(null) // framework slug whose components are loaded
  const [builds, setBuilds] = useState<SdkBuild[]>([])
  const [sdkVersion, setSdkVersion] = useState<SdkVersionInfo | null>(null)

  const [view, setView] = useState<'list' | 'wizard'>('list')
  const [step, setStep] = useState(1)
  const [build, setBuild] = useState<SdkBuild | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [creds, setCreds] = useState<CredentialsStatus | null>(null)
  const [gen, setGen] = useState<GenerateResult | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // Per-row in-flight actions — a SET, not the single `busy` scalar, so several rows can regenerate
  // (or download) at once and each keeps its own spinner instead of the signal jumping to the last click.
  const [regenerating, setRegenerating] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState<Set<string>>(new Set())
  const addId = (set: (fn: (p: Set<string>) => Set<string>) => void, id: string) => set((p) => new Set(p).add(id))
  const delId = (set: (fn: (p: Set<string>) => Set<string>) => void, id: string) => set((p) => { const n = new Set(p); n.delete(id); return n })

  const [name, setName] = useState('')
  const [framework, setFramework] = useState('react_native')
  const [buildSearch, setBuildSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [vendorSearch, setVendorSearch] = useState('')
  const [capFilter, setCapFilter] = useState('all')
  const [apps, setApps] = useState<AppConfig[]>([])
  const [activeApp, setActiveApp] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState<SdkBuild | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500)
  }

  useEffect(() => {
    if (!canAccess) return
    // Gate the list render on the LIGHTWEIGHT builds call only — it's the sole thing the list needs.
    ;(orgId ? appsSdkService.listBuilds(orgId) : Promise.resolve([] as SdkBuild[]))
      .then(setBuilds)
      .catch((e) => setError(e?.message || 'Could not reach the Apps SDK service'))
      .finally(() => setLoading(false))
    // Only the frameworks (tiny) are needed alongside the list — for the framework column + the
    // "New SDK" picker. The heavy vendor component catalog is NOT loaded here; it's fetched
    // per-framework when a build is opened/created (see the wizard effect below).
    appsSdkService.getFrameworks()
      .then((fws) => { setFrameworks(fws); if (fws[0]) setFramework(fws[0].slug) })
      .catch(() => {})
  }, [orgId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the vendor components ONLY when in the wizard, and ONLY for the current framework (the
  // backend filters by it). Covers open, new-build framework pick, and switching framework — the
  // list never pays for this.
  useEffect(() => {
    if (!canAccess || view !== 'wizard' || !framework || componentsFor === framework) return
    appsSdkService.getCatalog(framework)
      .then((cat) => { setComponents(cat.components); setComponentsFor(framework) })
      .catch(() => {})
  }, [view, framework, canAccess, componentsFor])

  // The gateway's products table is the source of truth for an app's catalog; this form only writes into it
  // (provisioning upserts, never deletes). So when an app's form has no products, load what the gateway already
  // holds instead of showing "No products yet" — Contraction Tracker's catalog lives only there.
  const [gatewayProducts, setGatewayProducts] = useState<Record<string, 'loading' | 'loaded' | 'none' | 'unavailable'>>({})
  useEffect(() => {
    if (!canAccess || view !== 'wizard') return
    const idx = activeApp
    const name = apps[idx]?.name
    if (!name || (apps[idx]?.iap?.products || []).length > 0 || gatewayProducts[name]) return
    setGatewayProducts((s) => ({ ...s, [name]: 'loading' }))
    appsSdkService.getGatewayProducts(name)
      .then((res) => {
        if (!res.available) { setGatewayProducts((s) => ({ ...s, [name]: 'unavailable' })); return }
        if (!res.products.length) { setGatewayProducts((s) => ({ ...s, [name]: 'none' })); return }
        // Only fill an app that is still the same app and still empty — never overwrite what was typed meanwhile.
        updateApp(idx, (d) => {
          if (d.name !== name || (d.iap?.products || []).length > 0) return
          d.iap ??= {}
          d.iap.products = res.products
        })
        setGatewayProducts((s) => ({ ...s, [name]: 'loaded' }))
      })
      .catch(() => setGatewayProducts((s) => ({ ...s, [name]: 'unavailable' })))
  }, [canAccess, view, activeApp, apps]) // eslint-disable-line react-hooks/exhaustive-deps

  // Base SDK version line — registry-backed, org-independent, best-effort (never blocks the page).
  useEffect(() => {
    if (!canAccess) return
    appsSdkService.getSdkVersion().then(setSdkVersion).catch(() => {})
  }, [canAccess])

  // URL → state: open the SDK + tab named by the `<buildId>_<step>` token (deep link, browser
  // back/forward, manual edit). Waits for the catalog so the framework/vendor filter resolves.
  useEffect(() => {
    if (!canAccess) return
    const parsed = parseTarget(target)
    if (!parsed) { if (view === 'wizard' && build) { setView('list'); setBuild(null) } return }
    if (!frameworks.length) return // catalog not ready yet — this effect re-runs once it loads
    if (view === 'wizard' && build?.id === parsed.id) {
      if (step !== parsed.step) {
        if (parsed.step >= 3 && !creds) appsSdkService.getCredentialsStatus(parsed.id).then(setCreds).catch(() => {})
        setStep(parsed.step)
      }
      return
    }
    loadBuildIntoWizard(parsed.id, parsed.step)
  }, [target, canAccess, frameworks.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // state → URL: keep the URL token in step with the open build + tab. Only writes in the wizard
  // (the list/new-build path navigates explicitly), so it never clobbers a deep link on first load.
  useEffect(() => {
    if (!canAccess || !orgSlug || view !== 'wizard' || !build?.id) return
    const base = `/${orgSlug}/admin/apps-sdk`
    const desired = `${base}/${buildTarget(build.id, step)}`
    const current = target ? `${base}/${target}` : base
    if (desired === current) return
    const cur = parseTarget(target)
    navigate(desired, { replace: !!(cur && cur.id === build.id) }) // step change = replace, open = push
  }, [view, build?.id, step, canAccess, orgSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  // Only the BUILD's framework — the catalog holds a row per (framework × vendor), so
  // without this the vendor list would show every framework's components (duplicates).
  const fwComponents = useMemo(() => {
    const fwId = build?.framework_id ?? frameworks.find((f) => f.slug === framework)?.id
    return fwId ? components.filter((c) => c.framework_id === fwId) : components
  }, [components, build, framework, frameworks])

  const byCapability = useMemo(() => {
    const g: Record<string, SdkComponent[]> = {}
    for (const c of fwComponents) (g[c.capability || 'other'] ??= []).push(c)
    if (g.ads) g.ads.sort((a, b) => Number(a.is_mediated) - Number(b.is_mediated))
    return g
  }, [fwComponents])

  const chosen = useMemo(() => fwComponents.filter((c) => selected.has(c.id)), [fwComponents, selected])
  const sizeIos = chosen.reduce((a, c) => a + (c.size_ios_bytes || 0), 0)
  const sizeAndroid = chosen.reduce((a, c) => a + (c.size_android_bytes || 0), 0)
  // Total = fixed SDK-core baseline (always present) + selected vendors' native footprint (ADR-004).
  const fw = frameworks.find((f) => f.slug === framework)
  const coreIos = fw?.core_size_ios_bytes || 0
  const coreAndroid = fw?.core_size_android_bytes || 0
  const totalIos = coreIos + sizeIos
  const totalAndroid = coreAndroid + sizeAndroid
  const capabilities = [...new Set(chosen.map((c) => c.capability).filter(Boolean))] as string[]
  // Install guide follows the BUILD's framework (not just the current selector).
  const buildFwSlug = frameworks.find((f) => f.id === build?.framework_id)?.slug || framework
  const installInfo = installGuide(buildFwSlug)
  const frameworkName = (b: SdkBuild) => frameworks.find((f) => f.id === b.framework_id)?.name
    || frameworks.find((f) => f.id === b.framework_id)?.slug || '—'
  const selectedMediated = chosen.filter((c) => c.is_mediated)
  const needsMediator = selectedMediated.length > 0 && !chosen.some(isMediator)
  const requiredVendors = creds?.vendors.filter((v) => v.required) || []
  const accountVendors = requiredVendors.filter((v) => v.kind === 'account')
  const managedVendors = requiredVendors.filter((v) => v.kind === 'managed')
  const perAppVendors = requiredVendors.filter((v) => v.integrator.slug && PER_APP_FIELDS[v.integrator.slug])
  const admobChosen = chosen.some((c) => c.integrator.slug === 'admob')
  const appVendorMissing = (a: AppConfig, slug: string): boolean => {
    const v = (a?.vendors?.[slug] || {}) as any
    if (slug === 'admob') return !((v.adUnits || []).some((u: any) => u.ios || u.android))
    if (slug === 'firebase') return !(v.config || v.iosConfig || v.androidConfig)
    if (slug === 'facebook') return !(v.appId && v.clientToken)
    if (slug === 'adapty') return !v.publicKey
    // Google UMP (the consent request) needs the AdMob app IDs and the app's privacy policy URL. With the
    // AdMob mediator selected it uses AdMob's own app ID field instead of a second copy.
    if (slug === 'ump') {
      const ids = admobChosen ? (a?.vendors?.admob || {}).appId : v.admobAppId
      return !(ids?.ios && ids?.android && a?.privacy?.privacyPolicyUrl)
    }
    return false
  }
  const appOk = (a: AppConfig) => !!a?.name && !!(a?.bundle?.ios || a?.bundle?.android) && perAppVendors.every((v) => !appVendorMissing(a, v.integrator.slug!))
  const accountsOk = accountVendors.every((v) => v.fields_set)
  const appsReady = apps.filter(appOk).length
  const readyClient = accountsOk && apps.length > 0 && appsReady === apps.length

  const updateApp = (idx: number, mutate: (d: any) => void) =>
    setApps((prev) => { const next = structuredClone(prev || []) as any[]; if (!next[idx]) next[idx] = {}; mutate(next[idx]); return next })
  const vSlug = (idx: number, slug: string): any => (apps[idx]?.vendors?.[slug] || {})
  const setPlat = (idx: number, slug: string, path: string, plat: string, val: string) =>
    updateApp(idx, (d) => { d.vendors ??= {}; d.vendors[slug] ??= {}; d.vendors[slug][path] ??= {}; d.vendors[slug][path][plat] = val })
  const setSingle = (idx: number, slug: string, path: string, val: string) =>
    updateApp(idx, (d) => { d.vendors ??= {}; d.vendors[slug] ??= {}; d.vendors[slug][path] = val })
  const setBundle = (idx: number, grp: 'bundle' | 'store', plat: string, val: string) =>
    updateApp(idx, (d) => { d[grp] ??= {}; d[grp][plat] = val })
  const setPrivacy = (idx: number, key: 'privacyPolicyUrl' | 'termsOfServiceUrl' | 'attUsageDescription', val: string) =>
    updateApp(idx, (d) => { d.privacy ??= {}; d.privacy[key] = val })
  const setAppName = (idx: number, val: string) => updateApp(idx, (d) => { d.name = val })
  const addAdUnit = (idx: number, slug: string) =>
    updateApp(idx, (d) => { d.vendors ??= {}; d.vendors[slug] ??= {}; d.vendors[slug].adUnits = [...(d.vendors[slug].adUnits || []), { placement: '', type: 'interstitial', ios: '', android: '' }] })
  const setAdUnit = (idx: number, slug: string, i: number, k: string, val: string) =>
    updateApp(idx, (d) => { d.vendors[slug].adUnits[i][k] = val })
  const rmAdUnit = (idx: number, slug: string, i: number) =>
    updateApp(idx, (d) => { d.vendors[slug].adUnits.splice(i, 1) })
  const setFirebaseFile = (idx: number, slot: 'iosConfig' | 'androidConfig', contents: string) =>
    updateApp(idx, (d) => { d.vendors ??= {}; d.vendors.firebase ??= {}; d.vendors.firebase[slot] = contents })
  // IAP catalog + store secrets (app.iap) — products register in glaium_iap.products at generation.
  const addProduct = (idx: number) =>
    updateApp(idx, (d) => { d.iap ??= {}; d.iap.products = [...(d.iap.products || []), { productId: '', type: 'consumable', name: '', price: '', grants: [] }] })
  const setProduct = (idx: number, i: number, k: string, val: any) =>
    updateApp(idx, (d) => { d.iap.products[i][k] = val })
  const rmProduct = (idx: number, i: number) =>
    updateApp(idx, (d) => { d.iap.products.splice(i, 1) })
  // Grandfathering: purchases of this product made before a date ALSO get another product's entitlements
  // (gateway grandfather_rules). Clearing sets null rather than deleting the key — null is what tells the
  // gateway to remove a stored rule; an absent key leaves it alone.
  const setGrandfather = (idx: number, i: number, patch: Record<string, string | null>) =>
    updateApp(idx, (d) => {
      const product = d.iap.products[i]
      product.grandfather = { countsAs: '', purchasedBefore: null, ...(product.grandfather || {}), ...patch }
    })
  const clearGrandfather = (idx: number, i: number) =>
    updateApp(idx, (d) => { d.iap.products[i].grandfather = null })
  const setIapCred = (idx: number, k: 'appleSharedSecret', val: string) =>
    updateApp(idx, (d) => { d.iap ??= {}; d.iap[k] = val })
  const toggleGrant = (idx: number, i: number, grant: string) =>
    updateApp(idx, (d) => {
      const product = d.iap.products[i]
      const g = new Set<string>(product.grants || [])
      if (grant === 'premium') {
        // The chip also shows isFullPremium (an app can be full premium without a `premium` grant), so it toggles
        // both together. Provisioning writes isFullPremium as false when absent, which is why it is always sent.
        const wasOn = g.has('premium') || !!product.isFullPremium
        if (wasOn) g.delete('premium'); else g.add('premium')
        product.isFullPremium = !wasOn
      } else if (g.has(grant)) {
        g.delete(grant)
      } else {
        g.add(grant)
      }
      product.grants = Array.from(g)
    })
  const addApp = () => setApps((p) => { const n = [...p, { name: `App ${p.length + 1}` }]; setActiveApp(n.length - 1); return n })
  const removeApp = (idx: number) => setApps((p) => { const n = p.filter((_, i) => i !== idx); setActiveApp(Math.max(0, Math.min(idx, n.length - 1))); return n })

  const filteredBuilds = builds
    .filter((b) => statusFilter === 'all' || b.status === statusFilter)
    .filter((b) => (b.name || '').toLowerCase().includes(buildSearch.toLowerCase()))

  // Every build is stamped with the base Glaium SDK (gateway) version, so a generated build is
  // "behind" when its version differs from the current latest base SDK. Versions are immutable +
  // monotonically increasing, so any mismatch means a newer one exists.
  const latestBase = sdkVersion?.version || null
  const updateAvailable = (b: SdkBuild) =>
    b.status === 'generated' && !!b.version && !!latestBase && b.version !== latestBase

  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4">
        <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center"><Lock className="h-8 w-8 text-slate-400" /></div>
        <h2 className="text-xl font-semibold text-slate-900">Access Restricted</h2>
        <p className="text-slate-500 max-w-md">The Apps SDK builder is available to managers, admins, and owners.</p>
      </div>
    )
  }

  function newBuild() { setNotice(null); navigate(`/${orgSlug}/admin/apps-sdk`); setView('wizard'); setStep(1); setBuild(null); setSelected(new Set()); setCreds(null); setGen(null); setName(''); setVendorSearch(''); setCapFilter('all'); setApps([{ name: 'App 1' }]); setActiveApp(0) }
  // Fetch a build by id and open the wizard at `step`. Shared by the row "Open" click and by the
  // URL→state sync (deep link). The URL is written by the state→URL effect, not here.
  async function loadBuildIntoWizard(id: string, step: number) {
    setBusy('load'); setNotice(null)
    try {
      const full = await appsSdkService.getBuild(id)
      setBuild(full); setSelected(new Set(full.component_ids || [])); setGen(null)
      setName(full.name || '')
      const slug = frameworks.find((f) => f.id === full.framework_id)?.slug
      if (slug) setFramework(slug)  // drives the framework-scoped vendor filter + summary
      setApps(full.apps?.length ? full.apps : [{ name: 'App 1' }]); setActiveApp(0)
      setVendorSearch(''); setCapFilter('all')
      // Steps 3–4 render off credentials-status; load it when landing there directly (deep link),
      // otherwise the normal step-2 "Continue" fills it. gen can't be reconstructed → step 4 shows Generate.
      setCreds(step >= 3 ? await appsSdkService.getCredentialsStatus(id).catch(() => null) : null)
      setView('wizard'); setStep(Math.min(4, Math.max(1, step)))
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not open that SDK'); setView('list'); navigate(`/${orgSlug}/admin/apps-sdk`)
    } finally { setBusy(null) }
  }
  async function openBuild(b: SdkBuild) { await loadBuildIntoWizard(b.id, 2) }
  async function backToList() { setView('list'); setBuild(null); navigate(`/${orgSlug}/admin/apps-sdk`); if (orgId) setBuilds(await appsSdkService.listBuilds(orgId)) }
  // Download a generated build's bundle straight from the list (no wizard round-trip). The wizard's
  // downloadBundle() needs the in-memory GenerateResult; this pulls the stored artifact by build id.
  async function downloadBuild(b: SdkBuild) {
    addId(setDownloading, b.id)
    try {
      // The SERVER names the file. Appending ".tgz" here was wrong for Unity, whose generator
      // packs a real zip — the bundle downloaded as a zip called .tgz, which neither unzip nor
      // tar will open. Only fall back to a local name if the header is missing, and leave the
      // extension off rather than assert one we do not know.
      const { blob, filename } = await appsSdkService.downloadArtifact(b.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || `glaium-apps-sdk-${String(b.name || b.id).replace(/[^\w.-]+/g, '-')}`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    } catch { setError('Download failed — generate the build first, then try again') }
    finally { delId(setDownloading, b.id) }
  }
  // Inline "update my SDK": re-run generation, which re-resolves the CURRENT latest base SDK and
  // rebuilds the bundle in place. No wizard round-trip — the build keeps its vendors/config.
  async function regenerateBuild(b: SdkBuild) {
    addId(setRegenerating, b.id)
    setNotice(null); setError(null)
    try {
      const res = await appsSdkService.generate(b.id)
      // Update ONLY this row from the result — not a full listBuilds() refetch. Concurrent
      // regenerates each refetching would race (a stale response can clobber a fresher one),
      // making a row look un-updated. A surgical per-row merge updates each independently.
      setBuilds((prev) => prev.map((x) => x.id === b.id ? {
        ...x,
        status: res.status,
        version: res.version,
        size_ios_bytes: res.size_ios_bytes,
        size_android_bytes: res.size_android_bytes,
        checksum: res.checksum,
        updated_at: new Date().toISOString(), // approx; a full reload later shows the server value
      } : x))
      // Surface the SAME outcome the wizard shows in step 4: the credential warning when some
      // selected vendors aren't connected, otherwise a plain success.
      if (res.credentials_incomplete?.length) {
        setNotice({
          kind: 'warning',
          text: `${res.credentials_incomplete.map((m) => m.integrator).join(', ')} not connected — the build installs, but those vendors won’t function until connected.`,
        })
      } else {
        setNotice({ kind: 'success', text: `Regenerated ${b.name || `SDK #${b.id}`}${res.version ? ` · v${res.version}` : ''}.` })
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Regenerate failed')
    } finally { delId(setRegenerating, b.id) }
  }
  async function doDeleteBuild(b: SdkBuild) {
    try { await appsSdkService.deleteBuild(b.id); if (orgId) setBuilds(await appsSdkService.listBuilds(orgId)) }
    catch (e: any) { setError(e?.response?.data?.detail || 'Delete failed') }
    finally { setDeleteTarget(null) }
  }
  function toggle(id: number) { setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n }); setCreds(null); setGen(null) }
  // Free navigation: jump to any tab at any point. Landing on Configure/Generate needs
  // credentials-status, which the forward "Continue" would normally load — fetch it here too.
  async function goStep(n: number) {
    setStep(n)
    if (n >= 3 && build && !creds) {
      try { setCreds(await appsSdkService.getCredentialsStatus(build.id)) } catch { /* leave null */ }
    }
  }

  async function step1Continue() {
    if (build) { setStep(2); return }  // editing an existing build — framework is fixed at creation
    if (!orgId) return
    setBusy('create')
    try {
      const b = await appsSdkService.createBuild({ organization_id: orgId, framework, name: name.trim() || 'Untitled SDK' })
      setBuild(b); setBuilds(await appsSdkService.listBuilds(orgId)); setStep(2)
    } catch (e: any) { setError(e?.response?.data?.detail || 'Create failed') } finally { setBusy(null) }
  }
  async function step2Continue() {
    if (!build) return
    setBusy('save')
    try { await appsSdkService.setComponents(build.id, [...selected]); setCreds(await appsSdkService.getCredentialsStatus(build.id)); setGen(null); setStep(3) }
    finally { setBusy(null) }
  }
  async function onSelectIntegration(integratorId: number, val: string) {
    if (val === '__new__') { window.open(`/${orgSlug}/integrations`, '_blank'); return }
    if (!build) return
    try { await appsSdkService.setIntegrationSelection(build.id, integratorId, Number(val)); setCreds(await appsSdkService.getCredentialsStatus(build.id)); setGen(null) }
    catch (e: any) { setError(e?.response?.data?.detail || 'Selection failed') }
  }
  async function saveAppConfig() {
    if (!build) return
    setBusy('savecfg')
    try {
      await appsSdkService.setAppConfig(build.id, apps)
      setCreds(await appsSdkService.getCredentialsStatus(build.id)); setGen(null)
    } catch (e: any) { setError(e?.response?.data?.detail || 'Save failed') } finally { setBusy(null) }
  }
  async function generate() {
    if (!build) return
    setBusy('generate')
    try { setGen(await appsSdkService.generate(build.id)); if (orgId) setBuilds(await appsSdkService.listBuilds(orgId)) }
    catch (e: any) { setError(e?.response?.data?.detail || 'Generate failed') } finally { setBusy(null) }
  }
  async function downloadBundle() {
    if (!build || !gen) return
    setBusy('download')
    try {
      const { blob, filename } = await appsSdkService.downloadArtifact(build.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      // Server header first, then gen.filename from the generate response — both already carry
      // the right extension. The last resort deliberately carries none rather than guessing
      // .tgz for a Unity bundle, which is a real zip.
      a.href = url; a.download = filename || gen.filename || `glaium-apps-sdk-${build.id}`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    } catch { setError('Download failed — please try again') } finally { setBusy(null) }
  }

  // A deep link (`…/<buildId>_<step>`) is still resolving into the wizard — keep showing the
  // skeleton instead of flashing the list for a frame before the URL→state effect swaps it in.
  const pendingDeepLink = (() => {
    const p = parseTarget(target)
    return !!p && !error && !(view === 'wizard' && build?.id === p.id) // release on error so we never stick
  })()
  if (loading || pendingDeepLink) return <div className="p-8 space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>

  const header = (
    <div className="flex items-start gap-3">
      <div className="rounded-xl bg-slate-900 p-2.5 shrink-0"><Package className="h-6 w-6 text-white" /></div>
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Apps SDK Builder</h1>
          <Badge variant="secondary">admin</Badge>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl mt-1">
          Assemble a tailored mobile SDK — pick the vendors an app needs, connect their accounts, and set the app's config, then generate the <b>self-contained config bundle</b> that installs it — no registry needed.
        </p>
        {sdkVersion?.version && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Boxes className="h-3.5 w-3.5 shrink-0" />
            <span>Base SDK <span className="font-medium text-foreground tabular-nums">v{sdkVersion.version}</span>
              {sdkVersion.published_at && fmtSdkDate(sdkVersion.published_at) && (
                <> · published {fmtSdkDate(sdkVersion.published_at)}</>
              )}
            </span>
          </p>
        )}
      </div>
    </div>
  )

  // ---- persistent summary rail (shown alongside the wizard steps) ----
  const summaryRail = (
    <div className="lg:sticky lg:top-6 space-y-3">
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Summary</div>
            <Badge variant="outline" className="capitalize gap-1.5"><FrameworkLogo slug={framework} className="h-3 w-3" />{(fw?.name || framework).replace('_', ' ')}</Badge>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">SDK name</div>
            <div className="text-sm font-medium truncate">{name || build?.name || 'Untitled SDK'}</div>
          </div>
          <Separator />
          <div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
              <span>Vendors</span><span>{chosen.length} selected</span>
            </div>
            {chosen.length === 0 ? (
              <div className="text-xs text-muted-foreground">None yet — add some in step 2.</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {chosen.map((c) => {
                  const m = CAPS[c.capability || ''] || null
                  return (
                    <span key={c.id} className="inline-flex items-center gap-1 rounded-md border bg-slate-50 px-1.5 py-0.5 text-[11px]">
                      {m && <span className={cn('h-1.5 w-1.5 rounded-full', m.dot)} />}
                      {c.integrator.name || c.integrator.slug}
                    </span>
                  )
                })}
              </div>
            )}
          </div>
          <Separator />
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5">Estimated size</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md bg-slate-50 p-2">
                <div className="text-[10px] text-muted-foreground">iOS</div>
                <div className="text-sm font-semibold tabular-nums">{mb(totalIos)}</div>
              </div>
              <div className="rounded-md bg-slate-50 p-2">
                <div className="text-[10px] text-muted-foreground">Android</div>
                <div className="text-sm font-semibold tabular-nums">{mb(totalAndroid)}</div>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">core {mb(coreIos)} + {chosen.length} vendors (iOS)</div>
          </div>
          {creds && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <div className="text-[11px] text-muted-foreground">Readiness</div>
                <div className="flex items-center gap-2 text-xs">
                  {accountsOk ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                  <span>Shared accounts {accountsOk ? 'connected' : 'incomplete'}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {appsReady === apps.length && apps.length > 0 ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                  <span>{appsReady} of {apps.length} app{apps.length === 1 ? '' : 's'} ready</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-6xl">
      {header}
      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 text-red-700 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}
      {view === 'list' && notice && (
        <div role="status" className={cn('flex items-start gap-2 rounded-lg border p-3 text-sm',
          notice.kind === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-green-200 bg-green-50 text-green-700')}>
          {notice.kind === 'warning' ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />}
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className={cn('ml-auto shrink-0', notice.kind === 'warning' ? 'text-amber-400 hover:text-amber-600' : 'text-green-500 hover:text-green-700')}>✕</button>
        </div>
      )}

      {view === 'list' ? (
        /* ---------------- BUILDS LIST ---------------- */
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={buildSearch} onChange={(e) => setBuildSearch(e.target.value)} placeholder="Search SDKs…" className="pl-8" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="generated">Generated</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={newBuild} disabled={!orgId}><Plus className="h-4 w-4 mr-1" /> New SDK</Button>
            </div>

            {filteredBuilds.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-14 gap-2">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center"><Boxes className="h-6 w-6 text-slate-400" /></div>
                <p className="font-medium">{builds.length === 0 ? 'No SDKs yet' : 'No SDKs match'}</p>
                <p className="text-sm text-muted-foreground max-w-xs">{builds.length === 0 ? 'Create your first tailored SDK bundle — pick vendors, connect accounts, and generate.' : 'Try a different search or status filter.'}</p>
                {builds.length === 0 && <Button onClick={newBuild} disabled={!orgId} className="mt-1"><Plus className="h-4 w-4 mr-1" /> New SDK</Button>}
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>SDK</TableHead>
                      <TableHead className="w-32">Framework</TableHead>
                      <TableHead className="w-28">Status</TableHead>
                      <TableHead className="w-60 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBuilds.map((b) => (
                      <TableRow key={b.id} className="cursor-pointer" onClick={() => openBuild(b)}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0"><Boxes className="h-4 w-4 text-slate-500" /></div>
                            <div className="min-w-0">
                              <div className="font-medium truncate">{b.name || `SDK #${b.id}`}</div>
                              <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                                {b.version ? <span className="tabular-nums">v{b.version}</span> : <span>not generated</span>}
                                {b.status === 'generated' && b.updated_at && fmtSdkDate(b.updated_at) && (
                                  <span>· generated {fmtSdkDate(b.updated_at)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-sm capitalize">
                            <FrameworkLogo slug={frameworks.find((f) => f.id === b.framework_id)?.slug || ''} className="h-3.5 w-3.5 text-slate-500" />
                            {frameworkName(b).replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {/* Per-row state: in-flight regenerate wins, then freshness ("Update
                              available"), then the plain "generated" lifecycle status. Reads the
                              per-row `regenerating` set so several rows update independently. */}
                          {regenerating.has(b.id) ? (
                            <Badge variant="secondary" className="gap-1">
                              <RefreshCw className="h-3 w-3 animate-spin" /> Regenerating…
                            </Badge>
                          ) : updateAvailable(b) ? (
                            <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700" title={`On v${b.version} — latest is v${latestBase}. Regenerate to update.`}>
                              <RefreshCw className="h-3 w-3" /> Update available
                            </Badge>
                          ) : (
                            <Badge variant={b.status === 'generated' ? 'default' : 'secondary'} className={cn('capitalize', b.status === 'generated' ? 'bg-green-600 hover:bg-green-600' : '')}>
                              {b.status === 'generated' ? <Check className="h-3 w-3 mr-1" /> : null}{b.status}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {b.status === 'generated' && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={(e) => { e.stopPropagation(); regenerateBuild(b) }}
                                disabled={regenerating.has(b.id)}
                                title={updateAvailable(b) ? `Update to the latest base SDK (v${latestBase})` : 'Regenerate with the latest base SDK'}
                                className={updateAvailable(b) ? 'text-amber-700 hover:text-amber-800 hover:bg-amber-50' : ''}>
                                <RefreshCw className={cn('h-4 w-4 mr-1', regenerating.has(b.id) && 'animate-spin')} />
                                {regenerating.has(b.id) ? 'Regenerating…' : updateAvailable(b) ? 'Update' : 'Regenerate'}
                              </Button>
                            )}
                            <Button
                              variant="ghost" size="sm"
                              onClick={(e) => { e.stopPropagation(); downloadBuild(b) }}
                              disabled={b.status !== 'generated' || !b.artifact_ref || downloading.has(b.id)}
                              title={b.status === 'generated' && b.artifact_ref ? 'Download the generated bundle' : 'Generate the build first to download'}>
                              <Download className={cn('h-4 w-4', downloading.has(b.id) && 'animate-pulse')} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openBuild(b) }}>Open <ChevronRight className="h-4 w-4 ml-0.5" /></Button>
                            <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(b) }} title="Delete" className="text-slate-300 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* ---------------- WIZARD ---------------- */
        <div className="space-y-5">
          <button onClick={backToList} className="text-sm text-muted-foreground hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back to SDKs</button>

          {/* Stepper */}
          <nav aria-label="Progress">
            <ol className="flex items-center gap-1">
              {STEPS.map((s, i) => {
                const done = step > s.n
                const active = step === s.n
                return (
                  <li key={s.n} className="flex items-center gap-1 flex-1">
                    <button
                      type="button"
                      onClick={() => goStep(s.n)}
                      aria-current={active ? 'step' : undefined}
                      className={cn('flex items-center gap-2.5 rounded-lg px-1.5 py-1 text-left hover:bg-slate-50 cursor-pointer')}>
                      <span className={cn('h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 transition-colors',
                        done ? 'bg-slate-900 text-white' : active ? 'border-2 border-slate-900 text-slate-900 ring-4 ring-slate-900/10' : 'border-2 border-slate-200 text-slate-400')}>
                        {done ? <Check className="h-4 w-4" /> : s.n}
                      </span>
                      <span className="hidden sm:block">
                        <span className={cn('block text-sm leading-none', active || done ? 'font-medium text-slate-900' : 'text-slate-400')}>{s.label}</span>
                        <span className="block text-[11px] text-muted-foreground mt-0.5">{s.hint}</span>
                      </span>
                    </button>
                    {i < STEPS.length - 1 && <div className={cn('h-0.5 flex-1 rounded-full', done ? 'bg-slate-900' : 'bg-slate-200')} />}
                  </li>
                )
              })}
            </ol>
          </nav>

          <div className={cn('grid gap-6', step >= 2 ? 'lg:grid-cols-[1fr_300px]' : '')}>
            {/* ---- left: step content + footer nav ---- */}
            <div className="space-y-5 min-w-0">
              <Card>
                <CardContent className="p-6">
                  {/* STEP 1 — Basics */}
                  {step === 1 && (
                    <div className="space-y-6 max-w-lg">
                      <div>
                        <h2 className="text-lg font-semibold">Let's name your SDK</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Give it a recognizable name and pick the target framework.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bn">SDK name</Label>
                        <Input id="bn" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monster Sort — ads + analytics" autoFocus />
                      </div>
                      <div className="space-y-2">
                        <Label>Framework</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {frameworks.map((f) => {
                            const on = framework === f.slug
                            const locked = !!build  // framework is fixed once the SDK is created
                            return (
                              <button key={f.slug} type="button" disabled={locked && !on} onClick={() => !locked && setFramework(f.slug)}
                                className={cn('flex items-center gap-2.5 rounded-lg border p-3 text-left transition-all',
                                  on ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900/10' : locked ? 'opacity-40 cursor-not-allowed' : 'hover:border-slate-300 hover:bg-slate-50/50')}>
                                <div className={cn('h-9 w-9 rounded-md flex items-center justify-center shrink-0', on ? 'bg-slate-900' : 'bg-slate-100')}>
                                  <FrameworkLogo slug={f.slug} className={cn('h-5 w-5', on ? (f.slug === 'unity' ? 'text-white' : 'text-[#61DAFB]') : 'text-slate-500')} />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium capitalize truncate">{(f.name || f.slug).replace('_', ' ')}</div>
                                  <div className="text-[11px] text-muted-foreground">{frameworkSub(f.slug)}</div>
                                </div>
                                {on && <CheckCircle2 className="h-4 w-4 text-slate-900 ml-auto shrink-0" />}
                              </button>
                            )
                          })}
                        </div>
                        {!!build && <p className="text-[11px] text-muted-foreground mt-1.5">Framework is set when the SDK is created and can't be changed.</p>}
                      </div>
                    </div>
                  )}

                  {/* STEP 2 — Vendors */}
                  {step === 2 && (
                    <div className="space-y-5">
                      <div>
                        <h2 className="text-lg font-semibold">Choose vendors</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Browse by capability. Only selected vendors are bundled — each adds to the SDK size.</p>
                      </div>
                      <div className="relative max-w-sm">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input value={vendorSearch} onChange={(e) => setVendorSearch(e.target.value)} placeholder="Search vendors…" className="pl-8" />
                      </div>
                      {/* Capability filter pills — keeps the catalog scannable as it grows */}
                      <div className="flex flex-wrap gap-1.5">
                        {['all', ...CAP_ORDER.filter((cap) => byCapability[cap]?.length)].map((cap) => {
                          const active = capFilter === cap
                          const meta = cap === 'all' ? null : CAPS[cap]
                          const count = cap === 'all' ? fwComponents.length : (byCapability[cap]?.length || 0)
                          return (
                            <button key={cap} type="button" onClick={() => setCapFilter(cap)}
                              className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                                active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 hover:bg-slate-50')}>
                              {meta && <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />}
                              {cap === 'all' ? 'All' : meta?.label || cap}
                              <span className={cn('tabular-nums', active ? 'text-white/70' : 'text-muted-foreground')}>{count}</span>
                            </button>
                          )
                        })}
                      </div>
                      {needsMediator && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                          <span><b>{selectedMediated.map((c) => c.integrator.name || c.integrator.slug).join(', ')}</b> need a mediator to run. Add <b>AppLovin MAX</b> or <b>AdMob</b> before continuing.</span>
                        </div>
                      )}
                      {(() => {
                        const anyMatch = CAP_ORDER.some((cap) => (byCapability[cap] || []).some((c) => (c.integrator.name || c.integrator.slug || '').toLowerCase().includes(vendorSearch.toLowerCase())))
                        if (!anyMatch) return (
                          <div className="flex flex-col items-center justify-center text-center py-10 gap-2">
                            <SearchX className="h-8 w-8 text-slate-300" />
                            <p className="text-sm font-medium">No vendors match “{vendorSearch}”</p>
                            <p className="text-xs text-muted-foreground">Try a different term or clear the search.</p>
                          </div>
                        )
                        return CAP_ORDER.filter((cap) => byCapability[cap]?.length && (capFilter === 'all' || capFilter === cap)).map((cap) => {
                          const meta = CAPS[cap] || { label: cap, icon: Boxes, accent: 'text-slate-600', dot: 'bg-slate-400' }
                          const Icon = meta.icon
                          const list = byCapability[cap].filter((c) => (c.integrator.name || c.integrator.slug || '').toLowerCase().includes(vendorSearch.toLowerCase()))
                          if (!list.length) return null
                          const groupSel = list.filter((c) => selected.has(c.id)).length
                          return (
                            <div key={cap} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Icon className={cn('h-4 w-4', meta.accent)} />
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{meta.label}</span>
                                {groupSel > 0 && <Badge variant="secondary" className="text-[10px]">{groupSel} selected</Badge>}
                              </div>
                              {cap === 'ads' && <p className="text-xs text-muted-foreground flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span><b className="text-slate-700">Mediators</b> (MAX, AdMob) run the auction; <b className="text-slate-700">mediated</b> networks ride inside one to raise fill.</span></p>}
                              {cap === 'ads' && !byCapability['privacy']?.length && <p className="text-xs text-violet-700 flex items-start gap-1.5 rounded-md bg-violet-50 border border-violet-200 px-2.5 py-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-violet-500" /><span>{buildFwSlug === 'unity' ? <><b>GDPR consent &amp; iOS App Tracking Transparency come with AppLovin MAX</b> in Unity — MAX runs its consent flow (Google UMP) and the ATT prompt at startup.</> : <><b>No GDPR consent request is available for this framework yet.</b></>}</span></p>}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {list.map((c) => {
                                  const on = selected.has(c.id)
                                  const orphan = c.is_mediated && on && !chosen.some(isMediator)
                                  return (
                                    <div key={c.id} onClick={() => toggle(c.id)}
                                      className={cn('relative flex items-center gap-2.5 rounded-lg border p-3 cursor-pointer transition-all',
                                        orphan ? 'border-amber-400 bg-amber-50' : on ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900/10' : 'hover:border-slate-300 hover:bg-slate-50/50')}>
                                      <Checkbox checked={on} className="pointer-events-none" />
                                      <BrandMark name={c.integrator.name || c.integrator.slug || ''} slug={c.integrator.slug || ''} className="h-7 w-7 text-[10px]" />
                                      <span className="flex-1 text-sm font-medium leading-tight min-w-0 truncate">{c.integrator.name || c.integrator.slug}</span>
                                      {isMediator(c) && <Badge className="text-[9px] px-1.5 bg-indigo-600 hover:bg-indigo-600">mediator</Badge>}
                                      {c.is_mediated && <Badge variant="outline" className="text-[9px] px-1 border-slate-300">mediated</Badge>}
                                      <span className="text-[11px] text-muted-foreground tabular-nums">{mb(c.size_ios_bytes)}</span>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button onClick={(e) => e.stopPropagation()} className="text-slate-300 hover:text-slate-600"><HelpCircle className="h-4 w-4" /></button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-72 text-sm" onClick={(e) => e.stopPropagation()}>
                                          <div className="font-medium mb-1">{c.integrator.name || c.integrator.slug}</div>
                                          <p className="text-muted-foreground">{c.description || 'No description available.'}</p>
                                          {c.docs_url && <a href={c.docs_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-xs inline-flex items-center gap-1 mt-2">Open docs <ExternalLink className="h-3 w-3" /></a>}
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })
                      })()}
                    </div>
                  )}

                  {/* STEP 3 — App configuration (shared accounts + a list of apps) */}
                  {step === 3 && creds && (
                    <div className="space-y-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <h2 className="text-lg font-semibold">Configure accounts &amp; apps</h2>
                          <p className="text-sm text-muted-foreground mt-0.5">Shared <b>account</b> keys come from Integrations; enter each app's <b>app-specific</b> values below.</p>
                        </div>
                        <Badge variant={readyClient ? 'default' : 'secondary'} className={readyClient ? 'bg-green-600 hover:bg-green-600' : ''}>{appsReady} of {apps.length} app{apps.length === 1 ? '' : 's'} ready</Badge>
                      </div>

                      {/* Shared accounts (build-level) */}
                      {accountVendors.length > 0 && (
                        <div className="rounded-lg border p-4 space-y-3">
                          <div className="text-sm font-medium">Shared accounts</div>
                          <p className="text-xs text-muted-foreground">The vendor account (and its SDK key) used across all apps in this SDK.</p>
                          {accountVendors.map((v) => (
                            <div key={v.integrator.id} className="space-y-1.5">
                              <div className="flex items-center gap-2 text-xs">
                                {v.fields_set ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                                <span className="font-medium">{v.integrator.name || v.integrator.slug}</span>
                                <span className="text-muted-foreground">— {(v.field_defs || []).map((f) => f.label || f.name).join(', ')}</span>
                              </div>
                              <Select value={v.selected_integration_id ? String(v.selected_integration_id) : undefined} onValueChange={(val) => onSelectIntegration(v.integrator.id, val)}>
                                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder={v.integrations?.length ? 'Select an account…' : 'No integration yet — create one'} /></SelectTrigger>
                                <SelectContent>
                                  {v.integrations?.map((ig) => <SelectItem key={ig.id} value={String(ig.id)}>#{ig.id} · {ig.name}{ig.fields_set ? '' : ' · no SDK key set'}</SelectItem>)}
                                  <SelectItem value="__new__" className="text-indigo-600">＋ Create new integration…</SelectItem>
                                </SelectContent>
                              </Select>
                              {v.selected_integration_id && !v.fields_set && (
                                <p className="text-[11px] text-amber-600">This account has no {(v.field_defs || []).map((f) => f.label || f.name).join(' / ')} yet — add it on the Integrations page.</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Glaium-managed vendors OTHER than the IAP gateway — the gateway/IAP is folded
                          into the In-app purchases panel below (its keys are auto-provisioned there). */}
                      {managedVendors.filter((v) => v.integrator.slug !== 'glaium_gateway').length > 0 && (
                        <div className="rounded-lg border border-green-200 bg-green-50/40 p-3 space-y-1">
                          {managedVendors.filter((v) => v.integrator.slug !== 'glaium_gateway').map((v) => (
                            <div key={v.integrator.id} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="font-medium">{v.integrator.name || v.integrator.slug}</span>
                              <span className="text-xs text-muted-foreground ml-auto">Provisioned by Glaium · per app</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Apps tabs */}
                      <div className="flex items-center gap-1 flex-wrap border-b pb-2">
                        {apps.map((a, i) => (
                          <button key={i} onClick={() => setActiveApp(i)}
                            className={cn('px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5', i === activeApp ? 'bg-slate-900 text-white' : 'hover:bg-slate-100')}>
                            {appOk(a) ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <AlertTriangle className={cn('h-3.5 w-3.5', i === activeApp ? 'text-amber-300' : 'text-amber-400')} />}
                            {a.name || `App ${i + 1}`}
                            {apps.length > 1 && <span onClick={(e) => { e.stopPropagation(); removeApp(i) }} className="ml-1 opacity-60 hover:opacity-100" title="Remove app"><Trash2 className="h-3 w-3" /></span>}
                          </button>
                        ))}
                        <Button type="button" variant="ghost" size="sm" onClick={addApp}><Plus className="h-4 w-4 mr-1" /> Add app</Button>
                      </div>

                      {/* Active app form */}
                      {apps[activeApp] && (
                        <div className="space-y-4">
                          {/* Decoy fields absorb Chrome/1Password autofill so real inputs stay clean. */}
                          <input type="text" name="_uf_user" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />
                          <input type="password" name="_uf_pass" autoComplete="new-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />
                          <div className="rounded-lg border p-4 space-y-3">
                            <div className="text-sm font-medium">App identity</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">App name</Label><Input value={apps[activeApp].name || ''} onChange={(e) => setAppName(activeApp, e.target.value)} placeholder="My App" {...NO_AUTOFILL} /></div>
                              <div className="space-y-1"><Label className="text-xs">Bundle ID (iOS)</Label><Input value={apps[activeApp].bundle?.ios || ''} onChange={(e) => setBundle(activeApp, 'bundle', 'ios', e.target.value)} placeholder="com.yourcompany.app" {...NO_AUTOFILL} /></div>
                              <div className="space-y-1"><Label className="text-xs">Bundle ID (Android)</Label><Input value={apps[activeApp].bundle?.android || ''} onChange={(e) => setBundle(activeApp, 'bundle', 'android', e.target.value)} placeholder="com.yourcompany.app" {...NO_AUTOFILL} /></div>
                              <div className="space-y-1"><Label className="text-xs">Store ID (iOS)</Label><Input value={apps[activeApp].store?.ios || ''} onChange={(e) => setBundle(activeApp, 'store', 'ios', e.target.value)} placeholder="App Store id (numeric)" {...NO_AUTOFILL} /></div>
                              <div className="space-y-1"><Label className="text-xs">Store ID (Android)</Label><Input value={apps[activeApp].store?.android || ''} onChange={(e) => setBundle(activeApp, 'store', 'android', e.target.value)} placeholder="Play package name" {...NO_AUTOFILL} /></div>
                              {/* Shown in the GDPR consent flow (MAX / Google UMP). Required for MAX apps. */}
                              <div className="space-y-1"><Label className="text-xs">Privacy policy URL</Label><Input value={apps[activeApp].privacy?.privacyPolicyUrl || ''} onChange={(e) => setPrivacy(activeApp, 'privacyPolicyUrl', e.target.value)} placeholder="https://yourcompany.com/privacy" {...NO_AUTOFILL} /></div>
                              <div className="space-y-1"><Label className="text-xs">Terms of service URL (optional)</Label><Input value={apps[activeApp].privacy?.termsOfServiceUrl || ''} onChange={(e) => setPrivacy(activeApp, 'termsOfServiceUrl', e.target.value)} placeholder="https://yourcompany.com/terms" {...NO_AUTOFILL} /></div>
                              {/* iOS tracking prompt text (NSUserTrackingUsageDescription), written into the app by the SDK. */}
                              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">iOS tracking prompt text (optional)</Label><Input value={apps[activeApp].privacy?.attUsageDescription || ''} onChange={(e) => setPrivacy(activeApp, 'attUsageDescription', e.target.value)} placeholder="Your data is used to show you more relevant ads and measure their performance." {...NO_AUTOFILL} /></div>
                            </div>
                          </div>

                          {perAppVendors.map((v) => {
                            const slug = v.integrator.slug || ''
                            const missing = appVendorMissing(apps[activeApp], slug)
                            // UMP's AdMob app ID field only when the AdMob mediator is NOT selected (then AdMob's field is used).
                            const fields = slug === 'ump' && admobChosen ? [] : perAppFieldsFor(slug)
                            return (
                              <div key={v.integrator.id} className={cn('rounded-lg border p-4 space-y-3', !missing ? 'border-green-200 bg-green-50/40' : 'border-amber-200 bg-amber-50/30')}>
                                <div className="flex items-center gap-2">
                                  {!missing ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                                  <span className="font-medium">{v.integrator.name || slug}</span>
                                  <span className="text-xs text-muted-foreground ml-auto">{missing ? 'needs app values' : 'ready'}</span>
                                </div>
                                {fields.map((f, fi) => {
                                  if (f.kind === 'adUnits') {
                                    const units = vSlug(activeApp, slug).adUnits || []
                                    return (
                                      <div key={fi} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                          <div className="text-xs font-medium">Ad units</div>
                                          <Button type="button" variant="outline" size="sm" onClick={() => addAdUnit(activeApp, slug)}><Plus className="h-3.5 w-3.5 mr-1" /> Add unit</Button>
                                        </div>
                                        {units.length === 0 && <p className="text-xs text-muted-foreground">No ad units yet. Add interstitial / rewarded / banner units as needed.</p>}
                                        {units.map((u: any, i: number) => (
                                          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.4fr_1.4fr_auto] gap-2 items-end">
                                            <div className="space-y-1"><Label className="text-[10px]">Placement</Label><Input className="h-8 text-xs" value={u.placement || ''} onChange={(e) => setAdUnit(activeApp, slug, i, 'placement', e.target.value)} placeholder="home" {...NO_AUTOFILL} /></div>
                                            <div className="space-y-1"><Label className="text-[10px]">Type</Label>
                                              <Select value={u.type || 'interstitial'} onValueChange={(val) => setAdUnit(activeApp, slug, i, 'type', val)}>
                                                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                                <SelectContent>{AD_UNIT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                                              </Select>
                                            </div>
                                            <div className="space-y-1"><Label className="text-[10px]">iOS unit id</Label><Input className="h-8 text-xs" value={u.ios || ''} onChange={(e) => setAdUnit(activeApp, slug, i, 'ios', e.target.value)} placeholder="ca-app-pub-…/…" {...NO_AUTOFILL} /></div>
                                            <div className="space-y-1"><Label className="text-[10px]">Android unit id</Label><Input className="h-8 text-xs" value={u.android || ''} onChange={(e) => setAdUnit(activeApp, slug, i, 'android', e.target.value)} placeholder="ca-app-pub-…/…" {...NO_AUTOFILL} /></div>
                                            <button type="button" onClick={() => rmAdUnit(activeApp, slug, i)} className="text-slate-300 hover:text-red-600 p-1 mb-1" title="Remove"><Trash2 className="h-4 w-4" /></button>
                                          </div>
                                        ))}
                                      </div>
                                    )
                                  }
                                  if (f.kind === 'firebase') {
                                    const fb = vSlug(activeApp, slug)
                                    const onFile = (slot: 'iosConfig' | 'androidConfig') => (e: any) => {
                                      const file = e.target.files?.[0]; if (!file) return
                                      const r = new FileReader(); r.onload = () => setFirebaseFile(activeApp, slot, String(r.result || '')); r.readAsText(file)
                                    }
                                    return (
                                      <div key={fi} className="space-y-2">
                                        <Label className="text-xs">Firebase config files</Label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <div className="text-[11px] text-muted-foreground">iOS — GoogleService-Info.plist</div>
                                            <input type="file" accept=".plist,.xml,text/xml" onChange={onFile('iosConfig')} className="block text-xs" />
                                            {fb.iosConfig && <span className="text-[10px] text-green-600 inline-flex items-center gap-1"><Check className="h-3 w-3" /> uploaded</span>}
                                          </div>
                                          <div className="space-y-1">
                                            <div className="text-[11px] text-muted-foreground">Android — google-services.json</div>
                                            <input type="file" accept=".json,application/json" onChange={onFile('androidConfig')} className="block text-xs" />
                                            {fb.androidConfig && <span className="text-[10px] text-green-600 inline-flex items-center gap-1"><Check className="h-3 w-3" /> uploaded</span>}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  }
                                  if (f.kind === 'platform') {
                                    const Field = f.secret ? SecretField : Input
                                    return (
                                      <div key={fi} className="space-y-1">
                                        <Label className="text-xs">{f.label}</Label>
                                        {f.tip && <div className="text-[11px] text-muted-foreground">{f.tip}</div>}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                          <Field type={f.secret ? 'password' : 'text'} {...NO_PM} autoComplete={f.secret ? 'new-password' : 'off'} name={`glm-${slug}-${f.path}-ios`} value={vSlug(activeApp, slug)[f.path]?.ios || ''} onChange={(e) => setPlat(activeApp, slug, f.path, 'ios', e.target.value)} placeholder={f.phIos || 'iOS'} />
                                          <Field type={f.secret ? 'password' : 'text'} {...NO_PM} autoComplete={f.secret ? 'new-password' : 'off'} name={`glm-${slug}-${f.path}-android`} value={vSlug(activeApp, slug)[f.path]?.android || ''} onChange={(e) => setPlat(activeApp, slug, f.path, 'android', e.target.value)} placeholder={f.phAndroid || 'Android'} />
                                        </div>
                                      </div>
                                    )
                                  }
                                  const Field = f.secret ? SecretField : Input
                                  return (
                                    <div key={fi} className="space-y-1">
                                      <Label className="text-xs">{f.label}</Label>
                                      {f.tip && <div className="text-[11px] text-muted-foreground">{f.tip}</div>}
                                      <Field type={f.secret ? 'password' : 'text'} {...NO_PM} autoComplete={f.secret ? 'new-password' : 'off'} name={`glm-${slug}-${f.path}`} value={vSlug(activeApp, slug)[f.path] || ''} onChange={(e) => setSingle(activeApp, slug, f.path, e.target.value)} placeholder={f.ph || ''} />
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          })}

                          {/* In-app purchases — product catalog (with grants) + store secrets. Registered
                              in glaium_iap.products at generation; grants drive the gateway entitlements. */}
                          {capabilities.includes('iap') && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-4 space-y-3">
                              <div className="flex items-center gap-2">
                                <ShoppingCart className="h-4 w-4 text-amber-600" />
                                <span className="font-medium">In-app purchases</span>
                                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Gateway keys auto-provisioned</span>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="text-xs font-medium">Products</div>
                                  <Button type="button" variant="outline" size="sm" onClick={() => addProduct(activeApp)}><Plus className="h-3.5 w-3.5 mr-1" /> Add product</Button>
                                </div>
                                {(apps[activeApp].iap?.products || []).length === 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    {gatewayProducts[apps[activeApp].name || ''] === 'loading'
                                      ? 'Checking the gateway for this app’s existing products…'
                                      : gatewayProducts[apps[activeApp].name || ''] === 'unavailable'
                                        ? 'Could not read this app’s products from the gateway. Existing products there are kept either way — saving never deletes them.'
                                        : 'No products yet. Add your store SKUs — the Product IDs must match your App Store Connect / Play Console in-app purchases.'}
                                  </p>
                                )}
                                {gatewayProducts[apps[activeApp].name || ''] === 'loaded' && (apps[activeApp].iap?.products || []).some((p) => p.platforms) && (
                                  <p className="text-xs text-muted-foreground">Loaded from the gateway — these products already exist for this app. Edits here update them; removing one here does not delete it there.</p>
                                )}
                                {(apps[activeApp].iap?.products || []).map((p, i) => (
                                  <div key={i} className="rounded-md border bg-white/60 p-2 space-y-2">
                                    <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_1fr_1.2fr_0.7fr_auto] gap-2 items-end">
                                      <div className="space-y-1"><Label className="text-[10px]">Product ID</Label><Input className="h-8 text-xs" value={p.productId || ''} onChange={(e) => setProduct(activeApp, i, 'productId', e.target.value)} placeholder="com.app.coins_1" {...NO_AUTOFILL} /></div>
                                      <div className="space-y-1"><Label className="text-[10px]">Type</Label>
                                        <Select value={p.type || 'consumable'} onValueChange={(val) => setProduct(activeApp, i, 'type', val)}>
                                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                          <SelectContent>{IAP_PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1"><Label className="text-[10px]">Name</Label><Input className="h-8 text-xs" value={p.name || ''} onChange={(e) => setProduct(activeApp, i, 'name', e.target.value)} placeholder="1,000 Coins" {...NO_AUTOFILL} /></div>
                                      <div className="space-y-1"><Label className="text-[10px]">Price USD</Label><Input className="h-8 text-xs" value={p.price || ''} onChange={(e) => setProduct(activeApp, i, 'price', e.target.value)} placeholder="4.99" {...NO_AUTOFILL} /></div>
                                      <button type="button" onClick={() => rmProduct(activeApp, i)} className="text-slate-300 hover:text-red-600 p-1 mb-1" title="Remove"><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Unlocks</span>
                                      {GRANT_OPTIONS.map((g) => {
                                        // Premium means "full premium": the gateway's isFullPremium, which an app can set without a
                                        // grant literally named `premium` (Contraction Tracker's premium_lifetime).
                                        const on = (p.grants || []).includes(g.key) || (g.key === 'premium' && !!p.isFullPremium)
                                        return (
                                          <button key={g.key} type="button" onClick={() => toggleGrant(activeApp, i, g.key)} title={g.desc}
                                            className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] transition', on ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500 hover:border-slate-300')}>
                                            {on && <Check className="h-3 w-3" />}{g.label}
                                          </button>
                                        )
                                      })}
                                      {/* App-specific grants the chips above cannot express (e.g. pdfExport). Shown so they are not mistaken
                                          for missing, and kept as they are when the form is saved. */}
                                      {(p.grants || []).filter((k) => !GRANT_OPTIONS.some((g) => g.key === k)).map((k) => (
                                        <span key={k} title="App-specific grant from the gateway; kept when saved" className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] text-slate-600">{k}</span>
                                      ))}
                                      {(p.grants || []).length === 0 && !p.isFullPremium && <span className="text-[10px] text-muted-foreground">· coins/consumables usually unlock nothing</span>}
                                    </div>
                                    {(() => {
                                      // Grandfathering needs another product to point at.
                                      const others = (apps[activeApp].iap?.products || []).filter((o, j) => j !== i && o.productId)
                                      if (others.length === 0) return null
                                      const gf = p.grandfather
                                      if (!gf) {
                                        return (
                                          <button type="button" onClick={() => setGrandfather(activeApp, i, {})}
                                            className="text-[11px] text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
                                            title="Honour a past promise: earlier buyers of this product also get another product's features">
                                            + Grandfather earlier buyers
                                          </button>
                                        )
                                      }
                                      return (
                                        <div className="flex items-center gap-2 flex-wrap rounded border border-amber-200 bg-amber-50/60 px-2 py-1.5">
                                          <span className="text-[10px] uppercase tracking-wide text-amber-700">Grandfather</span>
                                          <span className="text-[11px] text-slate-600">purchases before</span>
                                          <Input type="date" className="h-7 w-[9.5rem] text-xs"
                                            value={(gf.purchasedBefore || '').slice(0, 10)}
                                            onChange={(e) => setGrandfather(activeApp, i, { purchasedBefore: e.target.value || null })} />
                                          <span className="text-[11px] text-slate-600">also unlock</span>
                                          <Select value={gf.countsAs || ''} onValueChange={(val) => setGrandfather(activeApp, i, { countsAs: val })}>
                                            <SelectTrigger className="h-7 w-[11rem] text-xs"><SelectValue placeholder="product…" /></SelectTrigger>
                                            <SelectContent>{others.map((o) => <SelectItem key={o.productId} value={o.productId} className="text-xs">{o.name || o.productId}</SelectItem>)}</SelectContent>
                                          </Select>
                                          <Input className="h-7 flex-1 min-w-[10rem] text-xs" value={gf.reason || ''} placeholder="Why (shown to whoever edits this next)"
                                            onChange={(e) => setGrandfather(activeApp, i, { reason: e.target.value })} {...NO_AUTOFILL} />
                                          <button type="button" onClick={() => clearGrandfather(activeApp, i)} className="text-slate-400 hover:text-red-600 p-0.5" title="Remove grandfathering"><Trash2 className="h-3.5 w-3.5" /></button>
                                          <span className="basis-full text-[10px] text-muted-foreground">
                                            {gf.purchasedBefore ? 'Real purchases before this date keep it; later buyers get only this product.' : 'No date: every real purchase of this product gets it.'} Sandbox purchases never qualify. Applies when the SDK is generated.
                                          </span>
                                        </div>
                                      )
                                    })()}
                                  </div>
                                ))}
                              </div>

                              <div className="pt-1 text-xs font-medium">Receipt validation <span className="font-normal text-muted-foreground">— how the gateway verifies purchases with the stores</span></div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Apple shared secret <span className="font-normal text-muted-foreground">— subscriptions only</span></Label>
                                  <SecretField {...NO_PM} autoComplete="new-password" name="glm-iap-apple" value={apps[activeApp].iap?.appleSharedSecret || ''} onChange={(e) => setIapCred(activeApp, 'appleSharedSecret', e.target.value)} placeholder="Optional — App-Specific Shared Secret" />
                                  <div className="text-[10px] text-muted-foreground">Only needed if you sell auto-renewable subscriptions. App Store Connect → App Information.</div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Google Play <span className="font-normal text-muted-foreground">— Android only</span></Label>
                                  <div className="rounded-md border bg-slate-50 px-2 py-2 space-y-1.5">
                                    <div className="text-[11px] text-muted-foreground">In Play Console → <b>Users and permissions</b>, invite the Glaium validator below and grant it <b>View financial data</b> for this app (no key to upload — Glaium validates with its own account):</div>
                                    <div className="flex items-center gap-1.5">
                                      <code className="text-[11px] bg-white border rounded px-1.5 py-0.5 flex-1 truncate">{GLAIUM_PLAY_VALIDATOR}</code>
                                      <button type="button" onClick={() => copy(GLAIUM_PLAY_VALIDATOR, 'gpsa')} className="text-muted-foreground hover:text-foreground" title="Copy">
                                        {copied === 'gpsa' ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <p className="text-[11px] text-muted-foreground leading-relaxed"><b>Grants</b> flip in-app entitlements (Ad-free / Premium) once a purchase is validated. <b>Validation is server-side:</b> Android via the invited Glaium account above; iOS needs no setup for consumables/non-consumables (the shared secret is only for subscriptions). Products are registered in the gateway at generation and must match your store SKUs exactly.</p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={saveAppConfig} disabled={busy === 'savecfg'}>
                          {busy === 'savecfg' ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save &amp; re-check
                        </Button>
                        <span className="text-xs text-muted-foreground">Account keys live in Integrations; per-app values are saved to this SDK.</span>
                      </div>
                    </div>
                  )}

                  {/* STEP 4 — Review & generate */}
                  {step === 4 && build && (
                    <div className="space-y-5">
                      <div><h2 className="text-lg font-semibold">Review &amp; generate</h2><p className="text-sm text-muted-foreground mt-0.5">Confirm the build, then generate the self-contained bundle.</p></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">iOS total</div><div className="text-2xl font-semibold tabular-nums">{mb(totalIos)}</div><div className="text-[10px] text-muted-foreground">core {mb(coreIos)} + vendors {mb(sizeIos)}</div></div>
                        <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Android total</div><div className="text-2xl font-semibold tabular-nums">{mb(totalAndroid)}</div><div className="text-[10px] text-muted-foreground">core {mb(coreAndroid)} + vendors {mb(sizeAndroid)}</div></div>
                      </div>
                      <div className="space-y-1.5">
                        <div className="text-xs text-muted-foreground">{chosen.length} vendors · {capabilities.map((c) => CAPS[c]?.label || c).join(', ')}</div>
                        <div className="flex flex-wrap gap-1">{chosen.map((c) => <Badge key={c.id} variant="outline" className="text-[10px]">{c.integrator.name || c.integrator.slug}</Badge>)}</div>
                      </div>
                      <div className="text-sm text-muted-foreground">{apps.length} app{apps.length === 1 ? '' : 's'}: {apps.map((a) => a.name || 'Untitled').join(', ')}</div>
                      {creds && !creds.ready && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                          <span>{!accountsOk && 'Some shared accounts aren’t connected. '}{appsReady < apps.length && `${apps.length - appsReady} app(s) still need values. `}You can still generate, but those parts won’t function until completed.</span>
                        </div>
                      )}
                      <Separator />
                      {!gen ? (
                        <Button onClick={generate} disabled={busy === 'generate'} size="lg">
                          {busy === 'generate' ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Package className="h-4 w-4 mr-1" />} Generate SDK bundle
                        </Button>
                      ) : (
                        <div className="space-y-4">
                          <div className="rounded-lg border border-green-200 bg-green-50/60 p-4 space-y-3">
                            <div className="flex items-center gap-2 font-medium"><div className="h-8 w-8 rounded-full bg-green-600 flex items-center justify-center"><Sparkles className="h-4 w-4 text-white" /></div> Your SDK bundle is ready</div>
                            <div className="text-sm text-muted-foreground">Glaium SDK v{gen.version} · installs <code className="text-xs">{installInfo.pkg}</code></div>
                            {gen.credentials_incomplete.length > 0 && (
                              <div className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{gen.credentials_incomplete.map((m) => m.integrator).join(', ')} not connected — the build installs, but those vendors won’t function until connected.</span></div>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                              <Button onClick={downloadBundle} disabled={busy === 'download'}>{busy === 'download' ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Download bundle</Button>
                            </div>
                          </div>
                          <div className="rounded-lg border p-4 space-y-2">
                            <div className="text-sm font-medium">Install in the app</div>
                            <ol className="list-decimal ml-4 space-y-1.5 text-xs text-muted-foreground">
                              {installInfo.steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <a href={`/${orgSlug}/integrations`} className="rounded-lg border p-3 hover:border-slate-300 hover:bg-slate-50/50 transition-all">
                              <div className="flex items-center gap-2 text-sm font-medium"><Rocket className="h-4 w-4 text-slate-500" /> Verify events</div>
                              <p className="text-xs text-muted-foreground mt-1">Install, run the app, and confirm events arrive in your dashboard.</p>
                            </a>
                            <button onClick={newBuild} className="text-left rounded-lg border p-3 hover:border-slate-300 hover:bg-slate-50/50 transition-all">
                              <div className="flex items-center gap-2 text-sm font-medium"><BookOpen className="h-4 w-4 text-slate-500" /> Build another SDK</div>
                              <p className="text-xs text-muted-foreground mt-1">Assemble a bundle for another app or framework.</p>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Wizard footer nav */}
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => (step > 1 ? setStep(step - 1) : backToList())} disabled={busy != null}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> {step > 1 ? 'Back' : 'Cancel'}
                </Button>
                {step === 1 && <Button onClick={step1Continue} disabled={busy === 'create'}>{busy === 'create' ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Continue <ChevronRight className="h-4 w-4 ml-1" /></Button>}
                {step === 2 && <Button onClick={step2Continue} disabled={busy === 'save' || chosen.length === 0 || needsMediator}>{busy === 'save' ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Continue <ChevronRight className="h-4 w-4 ml-1" /></Button>}
                {step === 3 && <Button onClick={async () => { await saveAppConfig(); setStep(4) }} disabled={busy === 'savecfg'}>{busy === 'savecfg' ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null} Continue <ChevronRight className="h-4 w-4 ml-1" /></Button>}
                {step === 4 && <span />}
              </div>
            </div>

            {/* ---- right: summary rail (from step 2 on) ---- */}
            {step >= 2 && <div>{summaryRail}</div>}
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name || `SDK #${deleteTarget?.id}`}”?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the SDK build and its generated bundle. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteTarget && doDeleteBuild(deleteTarget)} className="bg-red-600 hover:bg-red-700">Delete SDK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
